import { Check } from 'lucide-react';
import type { ReactElement } from 'react';
import React from 'react';

import { Hint } from '@/components/occurra/Hint';
import { CancelIcon } from '@/components/occurra/icons/CancelIcon';
import { cn } from '@/lib/utils';
import type { OrgChoice } from '@/types/auth';

interface Props {
    choice: OrgChoice;
    /** The organization the chooser was reopened from. */
    isCurrent: boolean;
    disabled: boolean;
    /** Why the row is disabled, in a tooltip while it is. */
    disabledReason?: string;
    onPick: (choice: OrgChoice) => void;
    onDecline: (choice: OrgChoice) => void;
}

/** One entry of the login org chooser: a membership to enter, or an invitation to answer. */
export const OrgChoiceRow = (
    { choice, isCurrent, disabled: isDisabled, disabledReason, onPick, onDecline }: Props,
): ReactElement => {
    const inviter = choice.invitedByName || choice.invitedBy;
    const reason = isDisabled ? disabledReason : undefined;
    return (
        <li className="flex items-center gap-1">
            <Hint label={reason}>
                <button
                    type="button"
                    disabled={isDisabled}
                    className={cn(
                        'group flex min-w-0 flex-1 items-center gap-3 rounded-lg border',
                        'border-occ-border-1 px-4 py-3 text-left',
                        'not-disabled:hover:bg-occ-foreground-25',
                        'disabled:text-occ-foreground-disabled',
                    )}
                    onClick={() => onPick(choice)}
                >
                    <span className="flex min-w-0 flex-1 flex-col gap-0.5">
                        <span className="truncate text-sm font-medium">
                            {choice.orgName || choice.orgId}
                        </span>
                        {choice.invited ? (
                            <span className={cn(
                                'flex min-w-0 items-center gap-1.5 text-xs',
                                'text-occ-foreground-tertiary',
                                'group-disabled:text-occ-foreground-disabled',
                            )}
                            >
                                {choice.invitedByPicture && (
                                    <img
                                        src={choice.invitedByPicture}
                                        alt=""
                                        referrerPolicy="no-referrer"
                                        className="size-4 shrink-0 rounded-full object-cover"
                                    />
                                )}
                                <span className="truncate">
                                    {inviter ? (
                                        <>
                                            Invited by
                                            {' '}
                                            {inviter}
                                        </>
                                    ) : 'Invited'}
                                </span>
                            </span>
                        ) : (
                            choice.role && (
                                <span className={cn(
                                    'text-xs text-occ-foreground-tertiary',
                                    'group-disabled:text-occ-foreground-disabled',
                                )}
                                >
                                    {choice.role}
                                </span>
                            )
                        )}
                    </span>
                    {choice.invited && (
                        <span className={cn(
                            'shrink-0 rounded-full border border-occ-brand px-2 py-0.5',
                            'text-xs font-medium text-occ-brand',
                            'group-disabled:border-occ-foreground-50',
                            'group-disabled:text-occ-foreground-disabled',
                        )}
                        >
                            invited
                        </span>
                    )}
                    {isCurrent && (
                        <Check
                            className={cn(
                                'size-4 shrink-0 text-occ-brand',
                                'group-disabled:text-occ-foreground-disabled',
                            )}
                            aria-label="Current organization"
                        />
                    )}
                </button>
            </Hint>
            {choice.invited && (
                <Hint label={reason ?? 'Decline this invitation'}>
                    <button
                        type="button"
                        disabled={isDisabled}
                        className={cn(
                            'flex size-8 shrink-0 items-center justify-center rounded-full',
                            'text-occ-foreground-tertiary',
                            'not-disabled:hover:bg-occ-foreground-25',
                            'not-disabled:hover:text-occ-foreground-main',
                            'disabled:text-occ-foreground-disabled',
                        )}
                        onClick={() => onDecline(choice)}
                        aria-label="Decline this invitation"
                    >
                        <CancelIcon className="size-4" />
                    </button>
                </Hint>
            )}
        </li>
    );
};
